import * as i0 from '@angular/core';
import { EventEmitter, Component, Optional, ViewChild, Input, Output, NgModule } from '@angular/core';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import OktaSignIn from '@okta/okta-signin-widget';
import { of } from 'rxjs';
import { tap, mergeMap, delay } from 'rxjs/operators';
import * as i1 from '@roosevelt/common-ui-lib/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@roosevelt/common-ui-lib/core';
import { NotificationComponent, NotificationModule } from '@roosevelt/common-ui-lib/notification';
import { LoaderComponent, LoaderModule } from '@roosevelt/common-ui-lib/loader';
import * as i2 from '@okta/okta-auth-js';

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
const DEFAULT_ORIGINAL_URI = window.location.origin;
/**
 * Sign In Component
 *
 * @description a component to facilitate logging into roosevelt apps
 * @example
 * <!-- rs-doc {link: 'Custom Sign In Example', data: { loadSignin: false, optionalMFA: false, useRedirect: false, showForgotPassword: false, isError: false, redirectLogic: function(username){ return new Promise(res => res(`https://ddpa-auth.deltadental.com/authorize?audience=https%3A%2F%2Fportal.deltadental.auth0&connection=DDPA-User-Store-Tenant&client_id=51E73vLuZ3DnfYFk4i1jqpWfN6sFG5LQ&redirect_uri=https%3A%2F%2Fidentity.deltadental.com%2Faccounts%2F&scope=openid%20profile%20email%20offline_access&response_type=code&response_mode=query&state=a2drZm1CUFhnUHBBVFQxdzdqR3VNSGFzekxmMkQ2a3owaTVJc1M4SjdNMA%3D%3D&nonce=eXNEZC5HZFFMbkN4M1Jufmo3UWs1OWVPeXY2LS5rZlE3a3dMZ34uZ283Qg%3D%3D&code_challenge=HbYG-V3JDyhyN5K3_-8VQ4zVXKGDovYyNVimAzXDUfI&code_challenge_method=S256&auth0Client=eyJuYW1lIjoiYXV0aDAtcmVhY3QiLCJ2ZXJzaW9uIjoiMS4xMi4xIn0%3D&login_hint=${username}`)); }, redirectError: function(username){ throw new Error('fail') }  }} -->
 * <div style="display: flex; align-items: center; padding: 20px;">
 *   <div>
 *     <p>Redirection</p>
 *     <rs-checkbox style="display: block" (change)="useRedirect = !useRedirect" [checked]="useRedirect" label="Use Redirect Lookup"></rs-checkbox>
 *     <rs-checkbox style="display: block" (change)="isError = !isError" [checked]="!isError" label="Fake Call Should Succeed"></rs-checkbox>
 *   </div>
 *   <div>
 *     <p>UI Updates</p>
 *     <rs-checkbox style="display: block" (change)="optionalMFA = !optionalMFA" [checked]="optionalMFA" label="Allow MFA to be Optional"></rs-checkbox>
 *     <rs-checkbox style="display: block" (change)="showForgotPassword = !showForgotPassword" [checked]="showForgotPassword" label="Show Forgot Password"></rs-checkbox>
 *   </div>
 *   <button rs-button style="margin-bottom: 12px" (click)="loadSignin = !loadSignin">{{loadSignin ? 'Hide Sign In' : 'Load Sign In'}}</button>
 * </div>
 * <rs-sign-in *ngIf="loadSignin" [bypassOptionalMFA]="optionalMFA" [showForgotPassword]="showForgotPassword" [redirectSignIn]="useRedirect ? { template: redirectMessage, errorTemplate: redirectErrorMessage, shouldSignInRedirect: !isError ? redirectLogic : redirectError } : undefined" #signIn></rs-sign-in>
 * <ng-template #redirectMessage>
 *   <p style="margin-top:0; display: flex; align-items:center; justify-content: center;">We found your account within the Delta Dental system. We will redirect you to that site in a few seconds.</p>
 * </ng-template>
 * <ng-template #redirectErrorMessage>
 *   <p style="margin-top:0; display: flex; align-items:center; justify-content: center;">
 *     Sorry for the inconvenience. If you would like to register for a Dental Office Toolkit account, please register here
 *   </p>
 *   <div style="display: flex; align-items:center; justify-content: center; padding: 10px;">
 *     <button class="btn-center" rs-outlined-button>Register</button>
 *     <button class="btn-center" rs-button>Refresh</button>
 *   </div>
 * </ng-template>
 */
class SignInComponent {
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    /** allow users to bypass MFA */
    get bypassOptionalMFA() {
        return this._bypassOptionalMFA;
    }
    set bypassOptionalMFA(value) {
        this._bypassOptionalMFA = coerceBooleanProperty(value);
    }
    /** should the users get a chance to go through the forgot password flow */
    get showForgotPassword() {
        return this._showForgotPassword;
    }
    set showForgotPassword(value) {
        this._showForgotPassword = coerceBooleanProperty(value);
    }
    /** if your users have a clock that is skewed more than 5 mins on average. default is 15 mins. provide amount in seconds (900 = 15mins)*/
    get maxClockSkew() {
        return this._maxClockSkew;
    }
    set maxClockSkew(value) {
        this._maxClockSkew = coerceNumberProperty(value);
    }
    constructor(elementRef, config, oktaAuth, dropInConfig) {
        this.elementRef = elementRef;
        this.config = config;
        this.oktaAuth = oktaAuth;
        this.dropInConfig = dropInConfig;
        this.loginHtmlForm = undefined;
        /** a way to change the top image, provided by host application */
        this.imageSrc = undefined;
        /** if you would like to redirect the user away from this sign in by using a side effect */
        this.redirectSignIn = undefined;
        /** Tells the listening application that the sign-in is 'ready' for user input */
        this.ready = new EventEmitter();
        this.watcher = this.visibiltyWatcher.bind(this);
        this.showNotification = undefined;
        this.showErrorNotification = undefined;
        this.ddpaUrl = undefined;
        this.isFetching = undefined;
        this._bypassOptionalMFA = undefined;
        this.signIn = undefined;
        this._color = 'primary';
        this._maxClockSkew = 900;
        this._showForgotPassword = undefined;
    }
    ngOnInit() {
        this.color = this.color;
        if (this.dropInConfig?.signIn?.imgSrc && !this.imageSrc) {
            this.imageSrc = this.dropInConfig.signIn.imgSrc;
        }
        // if the user is looking at this page, show the signin
        if (window.document.visibilityState !== 'hidden') {
            this.setUpSignIn();
        }
        window.document.removeEventListener('visibilitychange', this.watcher);
        window.document.addEventListener('visibilitychange', this.watcher);
    }
    ngAfterViewInit() {
        if (this.signIn) {
            this.generateSignIn();
        }
    }
    ngOnDestroy() {
        this.signIn.remove();
        window.document.removeEventListener('visibilitychange', this.watcher);
    }
    setUpSignIn() {
        this.verifyConfig();
        this.signIn = new OktaSignIn({
            /**
             * Note: when using the Sign-In Widget for an OIDC flow, you still
             * need to configure the base URL for your Okta Org. Here
             * we derive it from the given issuer for convenience.
             */
            baseUrl: this.config.issuer.split('/oauth2')[0],
            authParams: {
                maxClockSkew: this.maxClockSkew
            },
            clientId: this.config.clientId,
            redirectUri: this.config.redirectUri,
            logo: this.imageSrc,
            features: {
                multiOptionalFactorEnroll: !!this.bypassOptionalMFA
            },
            i18n: {
                en: {
                    'primaryauth.title': ' ',
                    forgotpassword: !this.showForgotPassword ? ' ' : undefined
                },
            },
            authClient: this.oktaAuth,
            ...(this.redirectSignIn ? {
                // eslint-disable-next-line no-unused-vars
                transformUsername: (username, operation) => {
                    const el = document.querySelector('.auth-content');
                    el.style.visibility = 'hidden';
                    of('').pipe(tap(() => this.isFetching = true), mergeMap(() => this.redirectSignIn.shouldSignInRedirect(username))).subscribe(urlToRedirect => {
                        this.isFetching = false;
                        if (urlToRedirect) {
                            this.ddpaUrl = urlToRedirect;
                            this.showNotification = true;
                            of('').pipe(delay(this.redirectSignIn.delay || 10000)).subscribe(() => {
                                this.goToRedirect();
                            });
                        }
                        else {
                            el.style.visibility = 'visible';
                        }
                    }, (e) => {
                        this.showErrorNotification = true;
                        this.isFetching = false;
                    });
                    return username;
                }
            } : {})
        });
        // When navigating to a protected route, the route path is saved as the `originalUri`
        // If no `originalUri` has been saved, then redirect back to the app root
        const originalUri = this.oktaAuth.getOriginalUri();
        if (!originalUri || originalUri === DEFAULT_ORIGINAL_URI) {
            this.oktaAuth.setOriginalUri('/');
        }
        // Search for URL Parameters to see if a user is being routed to the application to recover password
        const searchParams = new URL(window.location.href).searchParams;
        this.signIn.otp = searchParams.get('otp');
        this.signIn.state = searchParams.get('state');
        this.signIn.on('ready', () => this.ready.emit(true));
    }
    goToRedirect() {
        window.location.href = this.ddpaUrl;
    }
    generateSignIn() {
        this.signIn.showSignInToGetTokens({
            el: '#sign-in-widget',
            scopes: this.config.scopes,
            authParams: {
                maxClockSkew: this.maxClockSkew
            }
        }).then((tokens) => {
            // Remove the widget
            this.signIn.remove();
            // In this flow the redirect to Okta occurs in a hidden iframe
            this.oktaAuth.handleLoginRedirect(tokens);
        }).catch((err) => {
            // Typically due to misconfiguration
            throw err;
        });
    }
    visibiltyWatcher() {
        if (window.document.visibilityState === 'hidden' && this.signIn) {
            this.signIn.remove();
            this.signIn = undefined;
        }
        else if (window.document.visibilityState !== 'hidden') {
            this.setUpSignIn();
            this.generateSignIn();
        }
    }
    verifyConfig() {
        let issues = [];
        if (!this.config.issuer) {
            issues = ['issuer'];
        }
        if (!this.config.redirectUri) {
            issues = [...issues, 'redirectUri'];
        }
        if (!this.config.scopes) {
            issues = [...issues, 'scopes'];
        }
        if (!this.config.clientId) {
            issues = [...issues, 'clientId'];
        }
        if (issues.length) {
            console.error('The follow BasicAuthConfig items are missing ' + issues.join(', '), 'This is the current config ' + JSON.stringify(this.config));
        }
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_bypassOptionalMFA = undefined; }
    static { this.ngAcceptInputType_maxClockSkew = undefined; }
    static { this.ngAcceptInputType_showForgotPassword = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SignInComponent, deps: [{ token: i0.ElementRef }, { token: i1.BasicAuthConfig }, { token: i2.OktaAuth }, { token: i1.CommonConfig, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SignInComponent, isStandalone: true, selector: "rs-sign-in", inputs: { imageSrc: "imageSrc", redirectSignIn: "redirectSignIn", color: "color", bypassOptionalMFA: "bypassOptionalMFA", showForgotPassword: "showForgotPassword", maxClockSkew: "maxClockSkew" }, outputs: { ready: "ready" }, viewQueries: [{ propertyName: "loginHtmlForm", first: true, predicate: ["nativeLoginForm"], descendants: true }], ngImport: i0, template: "<ng-container>\n  <div id=\"sign-in-widget\">\n    @if (isFetching && redirectSignIn) {\n      <rs-loader position=\"absolute\">\n        <p>Verifying Username</p>\n      </rs-loader>\n    }\n    @if ((showNotification || showErrorNotification) && redirectSignIn) {\n      <div\n        style=\"width: 400px; width: 400px; position: absolute; z-index: 99; left: calc(50% - 200px); top: 200px;\">\n        <rs-notification [type]=\"showNotification ? 'success' : 'error'\" visible baseZIndex=\"99\">\n          @if (redirectSignIn.template && showNotification) {\n            <ng-container\n              *ngTemplateOutlet=\"redirectSignIn.template\">\n            </ng-container>\n          }\n          @if (redirectSignIn.errorTemplate && showErrorNotification) {\n            <ng-container\n              *ngTemplateOutlet=\"redirectSignIn.errorTemplate\">\n            </ng-container>\n          }\n        </rs-notification>\n      </div>\n    }\n  </div>\n</ng-container>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host.rs-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;background-color:transparent;min-height:20px}:host.rs-button:disabled{cursor:not-allowed}:host.rs-button ::ng-deep>svg{vertical-align:middle}:host.rs-raised-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;box-shadow:0 6px 16px -1px #0000003d;border-radius:3px}:host.rs-raised-button:disabled{cursor:not-allowed}:host.rs-outlined-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;border-radius:3px;border-width:2px;background-color:transparent}:host.rs-outlined-button:disabled{cursor:not-allowed}:host ::ng-deep #okta-sign-in.auth-container .button-primary{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit}:host ::ng-deep #okta-sign-in.auth-container .button-primary:disabled{cursor:not-allowed}:host ::ng-deep #sign-in-widget{position:relative}:host ::ng-deep #sign-in-widget .loader-container{z-index:99}:host ::ng-deep .form-group{margin-top:5px}:host ::ng-deep rs-card>.rs-card{max-width:350px;margin:auto}.error-message{position:fixed;top:10px;margin:auto;left:0;right:0;align-items:center;justify-content:center;display:flex}.login-component{background:transparent;max-width:300px;padding:0;margin:auto;position:relative}.login-component button{margin-top:10px;width:100%}\n"], dependencies: [{ kind: "component", type: LoaderComponent, selector: "rs-loader", inputs: ["position", "imageSrc", "color"] }, { kind: "component", type: NotificationComponent, selector: "rs-notification", inputs: ["visible", "toast", "displayTimeout", "message", "type", "customConfig", "checkPasswordExpiry", "customExpirationMessage", "closeable"], outputs: ["visibleChange"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SignInComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-sign-in', standalone: true, imports: [LoaderComponent, NotificationComponent, NgTemplateOutlet], template: "<ng-container>\n  <div id=\"sign-in-widget\">\n    @if (isFetching && redirectSignIn) {\n      <rs-loader position=\"absolute\">\n        <p>Verifying Username</p>\n      </rs-loader>\n    }\n    @if ((showNotification || showErrorNotification) && redirectSignIn) {\n      <div\n        style=\"width: 400px; width: 400px; position: absolute; z-index: 99; left: calc(50% - 200px); top: 200px;\">\n        <rs-notification [type]=\"showNotification ? 'success' : 'error'\" visible baseZIndex=\"99\">\n          @if (redirectSignIn.template && showNotification) {\n            <ng-container\n              *ngTemplateOutlet=\"redirectSignIn.template\">\n            </ng-container>\n          }\n          @if (redirectSignIn.errorTemplate && showErrorNotification) {\n            <ng-container\n              *ngTemplateOutlet=\"redirectSignIn.errorTemplate\">\n            </ng-container>\n          }\n        </rs-notification>\n      </div>\n    }\n  </div>\n</ng-container>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host.rs-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;background-color:transparent;min-height:20px}:host.rs-button:disabled{cursor:not-allowed}:host.rs-button ::ng-deep>svg{vertical-align:middle}:host.rs-raised-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;box-shadow:0 6px 16px -1px #0000003d;border-radius:3px}:host.rs-raised-button:disabled{cursor:not-allowed}:host.rs-outlined-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;border-radius:3px;border-width:2px;background-color:transparent}:host.rs-outlined-button:disabled{cursor:not-allowed}:host ::ng-deep #okta-sign-in.auth-container .button-primary{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit}:host ::ng-deep #okta-sign-in.auth-container .button-primary:disabled{cursor:not-allowed}:host ::ng-deep #sign-in-widget{position:relative}:host ::ng-deep #sign-in-widget .loader-container{z-index:99}:host ::ng-deep .form-group{margin-top:5px}:host ::ng-deep rs-card>.rs-card{max-width:350px;margin:auto}.error-message{position:fixed;top:10px;margin:auto;left:0;right:0;align-items:center;justify-content:center;display:flex}.login-component{background:transparent;max-width:300px;padding:0;margin:auto;position:relative}.login-component button{margin-top:10px;width:100%}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.BasicAuthConfig }, { type: i2.OktaAuth }, { type: i1.CommonConfig, decorators: [{
                    type: Optional
                }] }], propDecorators: { loginHtmlForm: [{
                type: ViewChild,
                args: ['nativeLoginForm', { static: false }]
            }], imageSrc: [{
                type: Input
            }], redirectSignIn: [{
                type: Input
            }], color: [{
                type: Input
            }], bypassOptionalMFA: [{
                type: Input
            }], showForgotPassword: [{
                type: Input
            }], maxClockSkew: [{
                type: Input
            }], ready: [{
                type: Output
            }] } });

class SignInModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SignInModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: SignInModule, imports: [CommonModule,
            NotificationModule,
            LoaderModule,
            SignInComponent], exports: [SignInComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SignInModule, imports: [CommonModule,
            NotificationModule,
            LoaderModule,
            SignInComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SignInModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        NotificationModule,
                        LoaderModule,
                        SignInComponent
                    ],
                    providers: [],
                    exports: [SignInComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SignInComponent, SignInModule };
